<template>
  <Teleport to="body">
    <div
      v-if="state === 'warning' || state === 'expired'"
      class="session-timeout-overlay"
      @click="onBackdropClick"
    >
      <div class="session-timeout-card" @click.stop>
        <!-- Warning state -->
        <template v-if="state === 'warning'">
          <div class="timeout-ring-wrap">
            <svg class="timeout-ring" viewBox="0 0 120 120">
              <circle
                class="timeout-ring__track"
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke-width="6"
              />
              <circle
                class="timeout-ring__progress"
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke-width="6"
                stroke-linecap="round"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset"
              />
            </svg>
            <span class="timeout-ring__number">{{ countdown }}</span>
          </div>
          <h2 class="timeout-title">Session Timeout</h2>
          <p class="timeout-message">
            Your session is about to expire due to inactivity.
          </p>
          <button class="timeout-btn timeout-btn--primary" @click="stayLoggedIn">
            Stay Logged In
          </button>
        </template>

        <!-- Expired state -->
        <template v-else>
          <div class="timeout-icon-wrap">
            <svg class="timeout-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 class="timeout-title">Session Expired</h2>
          <p class="timeout-message">
            Your session has expired. Please log in again to continue.
          </p>
          <button class="timeout-btn timeout-btn--primary" @click="goToLogin">
            Log In
          </button>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useSessionTimeout } from '@modules/app/composables/useSessionTimeout';

const { state, countdown, stayLoggedIn, goToLogin } = useSessionTimeout();

const RADIUS = 52;
const circumference = 2 * Math.PI * RADIUS;
const dashOffset = computed(() => {
  const fraction = countdown.value / 60;
  return circumference * (1 - fraction);
});

function onBackdropClick() {
  // Only explicit button click dismisses the warning
}
</script>

<style scoped>
.session-timeout-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.session-timeout-card {
  background: var(--theme-bg-card, #fff);
  border: 1px solid var(--theme-border, #e5e7eb);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: calc(100% - 32px);
  padding: 40px 32px;
  text-align: center;
  animation: timeout-fade-in 0.25s ease-out;
}

@keyframes timeout-fade-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* ---- Countdown ring ---- */
.timeout-ring-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 24px;
}

.timeout-ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.timeout-ring__track {
  stroke: var(--theme-border, #e5e7eb);
}

.timeout-ring__progress {
  stroke: var(--theme-green, #1ca55c);
  transition: stroke-dashoffset 1s linear;
}

.timeout-ring__number {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: 700;
  color: var(--theme-text-primary, #1a1a1a);
  font-variant-numeric: tabular-nums;
}

/* ---- Lock icon ---- */
.timeout-icon-wrap {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  border-radius: 50%;
}

.timeout-lock-icon {
  width: 36px;
  height: 36px;
  color: #dc2626;
}

/* ---- Text ---- */
.timeout-title {
  /* Figma: App/H1 */
  font-family: 'Manrope', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.02em;
  color: var(--theme-text-primary, #151515);
  margin: 0 0 8px;
}

.timeout-message {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 28px;
  line-height: 1.5;
}

/* ---- Button ---- */
.timeout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 12px 32px;
  transition: background 0.15s, transform 0.1s;
  width: 100%;
}

.timeout-btn:active {
  transform: scale(0.98);
}

.timeout-btn--primary {
  background: var(--theme-green, #1ca55c);
  color: #fff;
}

.timeout-btn--primary:hover {
  background: #18944f;
}

/* ---- Mobile ---- */
@media (max-width: 480px) {
  .session-timeout-card {
    padding: 28px 20px;
  }

  .timeout-ring-wrap {
    width: 96px;
    height: 96px;
    margin-bottom: 20px;
  }

  .timeout-ring__number {
    font-size: 24px;
  }

  .timeout-title {
    font-size: 18px;
  }
}
</style>
